/**
 * Ruth tenglamasi — cho'kmali filtratsiya fizikasi.
 *
 * TENGLAMALAR
 *   Darsi (to'siq + cho'kma qarshiligi ketma-ket):
 *     Q = dV/dt = A²·ΔP / (μ·(α·c·V + R_m·A))
 *   O'zgarmas ΔP da integral (Ruth):
 *     t = (μ·α·c/(2·A²·ΔP))·V² + (μ·R_m/(A·ΔP))·V
 *   Kasatkin shakli:  V² + 2·C·V = K·t,   K = 2·A²·ΔP/(μ·α·c),   C = R_m·A/(α·c)
 *     → V(t) = −C + √(C² + K·t);  t/V = V/K + 2C/K  (chiziqli — K, C ni tajribadan aniqlash)
 *   Siqiluvchan cho'kma:  α = α_ref·(ΔP/ΔP_ref)^s
 *   O'zgarmas Q da:       ΔP(V) = Q·μ·(α(ΔP)·c·V + R_m·A)/A²   (s > 0 → oshkormas, Newton)
 *   Cho'kma qalinligi:    L = c·V / (A·ρ_s·(1 − ε))
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR (ASSUMPTION)
 *   1. c (kg qattiq / m³ filtrat) o'zgarmas; cho'kmadagi suyuqlik hajmi filtratga
 *      nisbatan kichik (c aynan filtrat hajmiga nisbatan berilgan deb olinadi).
 *   2. Siqiluvchan cho'kmada butun cho'kma qatlami JORIY ΔP dagi α ga ega
 *      (tarix hisobga olinmaydi; o'zgarmas bosimda bu aniq, o'zgarmas sarfda taxminiy).
 *   3. R_m o'zgarmas (to'siq tiqilib qolishi yo'q); cho'kma g'ovakligi ε o'zgarmas.
 *   4. Suyuqlik yopishqoqligi toza suyuqlikniki (eritilgan moddalar ta'siri yo'q), T o'zgarmas.
 *   5. Yuvish, quritish, cho'kmani tushirish bosqichlari modelga kirmaydi (faqat filtrlash).
 *
 * MANBA: Kasatkin, "Osnovnye protsessy i apparaty", filtrlash bobi (K va C konstantalar);
 *        Pavlov–Romankov–Noskov, filtrlash bo'limi (misollar); Perry's 8-nashr, 18-bob
 *        (Ruth, siqiluvchanlik ko'rsatkichi); McCabe–Smith–Harriott, 30-bob.
 */
import { viscosity as liquidViscosity } from '../../properties';
import { bisection, newtonScalar } from '../../solvers';
import type { WarningSink } from '../../types';
import type { FiltrationInputs, FiltrationParams } from './types';

export const DEFAULT_REFERENCE_PRESSURE = 1e5;
export const DEFAULT_TEMPERATURE = 293.15;

export function validateFiltrationParams(p: FiltrationParams): void {
  if (!(p.area > 0)) throw new Error('filtration: area must be > 0');
  if (!(p.mediumResistance >= 0)) throw new Error('filtration: mediumResistance must be ≥ 0');
  if (!(p.specificCakeResistance >= 0))
    throw new Error('filtration: specificCakeResistance must be ≥ 0');
  if (p.referencePressure !== undefined && !(p.referencePressure > 0))
    throw new Error('filtration: referencePressure must be > 0');
  const s = p.compressibility ?? 0;
  if (!(s >= 0 && s <= 1)) throw new Error('filtration: compressibility must be in [0, 1]');
  if (!(p.solidDensity > 0)) throw new Error('filtration: solidDensity must be > 0');
  if (!(p.cakePorosity >= 0 && p.cakePorosity < 1))
    throw new Error('filtration: cakePorosity must be in [0, 1)');
  if (!(p.maxCakeThickness > 0)) throw new Error('filtration: maxCakeThickness must be > 0');
  if (p.mode === 'constantRate' && !(p.maxPressure !== undefined && p.maxPressure > 0))
    throw new Error('filtration: maxPressure is required (> 0) for constantRate');
  if (p.maxPressure !== undefined && !(p.maxPressure > 0))
    throw new Error('filtration: maxPressure must be > 0');
  if (p.liquid === undefined && !(p.viscosity !== undefined && p.viscosity > 0))
    throw new Error('filtration: liquid or viscosity (> 0) is required');
}

export function validateFiltrationInputs(p: FiltrationParams, i: FiltrationInputs): void {
  if (!(i.solidsConcentration >= 0)) throw new Error('filtration: solidsConcentration must be ≥ 0');
  if (i.temperature !== undefined && !(i.temperature > 0))
    throw new Error('filtration: temperature must be > 0 K');
  if (p.mode === 'constantPressure' && !(i.pressureDrop !== undefined && i.pressureDrop >= 0))
    throw new Error('filtration: pressureDrop (≥ 0) is required for constantPressure');
  if (p.mode === 'constantRate' && !(i.flowRate !== undefined && i.flowRate >= 0))
    throw new Error('filtration: flowRate (≥ 0) is required for constantRate');
  if (i.targetVolume !== undefined && !(i.targetVolume > 0))
    throw new Error('filtration: targetVolume must be > 0');
  if (i.targetCakeThickness !== undefined && !(i.targetCakeThickness > 0))
    throw new Error('filtration: targetCakeThickness must be > 0');
}

/** μ, Pa·s — `liquid` dan (T da) yoki param */
export function viscosityOf(p: FiltrationParams, T: number, warnings?: WarningSink): number {
  if (p.liquid !== undefined) return liquidViscosity(p.liquid, 'liquid', T, warnings);
  return p.viscosity as number;
}

/** α(ΔP) = α_ref·(ΔP/ΔP_ref)^s, m/kg */
export function specificResistance(p: FiltrationParams, dP: number): number {
  const s = p.compressibility ?? 0;
  if (s === 0) return p.specificCakeResistance;
  const ratio = Math.max(0, dP) / (p.referencePressure ?? DEFAULT_REFERENCE_PRESSURE);
  return p.specificCakeResistance * Math.pow(ratio, s);
}

/** Cho'kma qalinligi L = c·V/(A·ρ_s·(1−ε)), m */
export function cakeThickness(p: FiltrationParams, c: number, V: number): number {
  return (c * V) / (p.area * p.solidDensity * (1 - p.cakePorosity));
}

/** Berilgan qalinlikka mos filtrat hajmi, m³ (c = 0 → Infinity) */
export function volumeForThickness(p: FiltrationParams, c: number, L: number): number {
  if (c <= 0) return Infinity;
  return (L * p.area * p.solidDensity * (1 - p.cakePorosity)) / c;
}

/** Oniy sarf Q = A²·ΔP/(μ·(α·c·V + R_m·A)), m³/s */
export function flowAt(
  p: FiltrationParams,
  mu: number,
  alpha: number,
  c: number,
  V: number,
  dP: number,
): number {
  if (dP <= 0) return 0;
  const R = alpha * c * V + p.mediumResistance * p.area;
  if (R <= 0) return Infinity;
  return (p.area * p.area * dP) / (mu * R);
}

/** Kasatkin konstantalari K (m⁶/s) va C (m³); α·c = 0 → Infinity */
export function ruthConstants(
  p: FiltrationParams,
  mu: number,
  alpha: number,
  c: number,
  dP: number,
): { K: number; C: number } {
  const ac = alpha * c;
  if (ac <= 0) return { K: Infinity, C: Infinity };
  return {
    K: (2 * p.area * p.area * dP) / (mu * ac),
    C: (p.mediumResistance * p.area) / ac,
  };
}

/** O'zgarmas ΔP da analitik t(V) (Ruth), s */
export function ruthTime(
  p: FiltrationParams,
  mu: number,
  alpha: number,
  c: number,
  dP: number,
  V: number,
): number {
  if (dP <= 0) return V > 0 ? Infinity : 0;
  const A = p.area;
  return ((mu * alpha * c) / (2 * A * A * dP)) * V * V + ((mu * p.mediumResistance) / (A * dP)) * V;
}

/** O'zgarmas ΔP da analitik V(t) (Ruth), m³ */
export function ruthVolume(
  p: FiltrationParams,
  mu: number,
  alpha: number,
  c: number,
  dP: number,
  t: number,
): number {
  const { K, C } = ruthConstants(p, mu, alpha, c, dP);
  if (!Number.isFinite(K)) return ((p.area * dP) / (mu * p.mediumResistance)) * t;
  return -C + Math.sqrt(C * C + K * t);
}

/**
 * O'zgarmas Q rejimida V hajmda talab qilinadigan ΔP, Pa.
 * s = 0 → oshkor; s > 0 → f(ΔP) = ΔP − Qμ(α(ΔP)cV + R_mA)/A² = 0 (Newton, zaxira bisection).
 * Yechim yo'q bo'lsa (s = 1 va cho'kma juda katta) → Infinity.
 */
export function pressureForRate(
  p: FiltrationParams,
  mu: number,
  c: number,
  V: number,
  Q: number,
): number {
  if (Q <= 0) return 0;
  const A = p.area;
  const base = (Q * mu * p.mediumResistance * A) / (A * A);
  const s = p.compressibility ?? 0;
  const coef = (Q * mu * c * V) / (A * A); // ΔP = base + coef·α(ΔP)
  if (s === 0 || coef === 0) return base + coef * p.specificCakeResistance;
  const f = (dP: number) => dP - base - coef * specificResistance(p, dP);
  const x0 = base + coef * p.specificCakeResistance;
  const nr = newtonScalar(f, Math.max(x0, 1), { bounds: [0, 1e15], xtol: 1e-12, ftol: 1e-9 });
  if (nr.converged && nr.value > 0) return nr.value;
  // Bracket: f(0) = −base < 0; yuqori chegara qidiriladi
  let hi = Math.max(x0, 1);
  for (let i = 0; i < 60 && f(hi) < 0; i++) hi *= 2;
  if (f(hi) < 0) return Infinity;
  const bs = bisection(f, 0, hi, { xtol: 1e-12, ftol: 1e-9 });
  return bs.converged ? bs.value : Infinity;
}
